import React, { useEffect, useMemo, useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { marketingAPI } from '../services/api';
import { Spinner } from '../components/Spinner';
import { Plus, Users, Target, Calendar, Bell } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/formatters';

const headerCellClass = 'px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500';
const bodyCellClass = 'px-6 py-4 text-sm text-slate-500';

const MarketingLayout = () => (
  <Routes>
    <Route index element={<MarketingOverview />} />
    <Route path="campaigns" element={<Campaigns />} />
    <Route path="customers" element={<CustomerSegments />} />
    <Route path="notes" element={<CustomerNotes />} />
    <Route path="reminders" element={<Reminders />} />
  </Routes>
);

const MarketingOverview = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await marketingAPI.getAnalytics();
        setStats(response.data);
      } catch (error) {
        console.error('Pazarlama istatistikleri alınamadı:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <Spinner />;
  }

  const cards = [
    { title: 'Toplam Kampanya', value: stats.totalCampaigns || 0, icon: Target, color: 'text-blue-600 bg-blue-100' },
    { title: 'Aktif Kampanya', value: stats.activeCampaigns || 0, icon: Users, color: 'text-green-600 bg-green-100' },
    { title: 'Toplam Bütçe', value: formatCurrency(stats.totalBudget), icon: Calendar, color: 'text-purple-600 bg-purple-100' },
    { title: 'Hatırlatma', value: stats.notesWithReminders || 0, icon: Bell, color: 'text-orange-600 bg-orange-100' }
  ];

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Pazarlama</h1>
            <p className="mt-1 text-sm text-slate-500">Kampanyalar, müşteri notları ve hatırlatmaları aynı akışta yönetin.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => navigate('/marketing/campaigns')} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Kampanyalar</button>
            <button onClick={() => navigate('/marketing/customers')} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Segmentler</button>
            <button onClick={() => navigate('/marketing/reminders')} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">Hatırlatmalar</button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.title} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className={`rounded-xl p-3 ${card.color}`}><Icon className="h-5 w-5" /></div>
                <div>
                  <p className="text-sm font-medium text-slate-500">{card.title}</p>
                  <p className="text-2xl font-bold text-slate-900">{card.value}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Bütçesi En Yüksek Kampanyalar</h2>
          <div className="mt-4 space-y-3">
            {stats.topBudgetCampaigns?.slice(0, 5).map((campaign, index) => (
              <div key={index} className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900">{campaign.name}</p>
                  <p className="text-xs text-slate-500 capitalize">{campaign.status}</p>
                </div>
                <span className="text-sm font-semibold text-slate-900">{formatCurrency(campaign.budget)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Hızlı İşlemler</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <button onClick={() => navigate('/marketing/campaigns')} className="rounded-xl border border-slate-200 px-4 py-4 text-left hover:bg-slate-50"><p className="text-sm font-semibold text-slate-900">Kampanyalar</p><p className="mt-1 text-xs text-slate-500">Yeni kayıt</p></button>
            <button onClick={() => navigate('/marketing/customers')} className="rounded-xl border border-slate-200 px-4 py-4 text-left hover:bg-slate-50"><p className="text-sm font-semibold text-slate-900">Segmentler</p><p className="mt-1 text-xs text-slate-500">En iyi müşteriler</p></button>
            <button onClick={() => navigate('/marketing/notes')} className="rounded-xl border border-slate-200 px-4 py-4 text-left hover:bg-slate-50"><p className="text-sm font-semibold text-slate-900">Notlar</p><p className="mt-1 text-xs text-slate-500">İletişim takibi</p></button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await marketingAPI.getCampaigns();
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      toast.error('Kampanyalar alınamadı');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Kampanyalar</h1>
          <p className="mt-1 text-sm text-slate-500">Aktif ve tamamlanan kampanyaları kart düzeninde inceleyin.</p>
        </div>
        <button className="flex items-center rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Kampanya Oluştur
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
        {campaigns.map((campaign) => (
          <div key={campaign.id} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-slate-900">{campaign.name}</h3>
              <span className={`rounded-md px-2 py-1 text-xs font-semibold ${
                campaign.status === 'active' ? 'bg-green-100 text-green-800' :
                campaign.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                campaign.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {campaign.status}
              </span>
            </div>
            
            <p className="mb-4 text-sm text-slate-600">{campaign.description}</p>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Bütçe:</span>
                <span className="font-semibold">{formatCurrency(campaign.budget)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Başlangıç:</span>
                <span>{new Date(campaign.start_date).toLocaleDateString('tr-TR')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Bitiş:</span>
                <span>{new Date(campaign.end_date).toLocaleDateString('tr-TR')}</span>
              </div>
            </div>
            
            <div className="mt-4 flex justify-end space-x-2 border-t border-slate-200 pt-4">
              <button className="text-sm text-blue-600 hover:text-blue-900">Düzenle</button>
              <button className="text-red-600 hover:text-red-900 text-sm">Sil</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const CustomerSegments = () => {
  const [segments, setSegments] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSegments();
  }, []);

  const fetchSegments = async () => {
    try {
      const response = await marketingAPI.getCustomerSegments();
      setSegments(response.data);
    } catch (error) {
      toast.error('Müşteri segmentleri alınamadı');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Müşteri Segmentleri</h1>
        <p className="mt-1 text-sm text-slate-500">Müşteri yoğunluğunu ve en iyi harcama yapanları tek ekranda görüntüleyin.</p>
      </div>
      
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-medium text-slate-500 mb-2">Toplam Müşteri</h3>
          <p className="text-3xl font-bold text-slate-900">{segments.total || 0}</p>
        </div>
        
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-medium text-slate-500 mb-2">Aktif Müşteri</h3>
          <p className="text-3xl font-bold text-green-600">{segments.active || 0}</p>
        </div>
        
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-medium text-slate-500 mb-2">VIP Müşteri</h3>
          <p className="text-3xl font-bold text-purple-600">{segments.vip || 0}</p>
        </div>
        
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-medium text-slate-500 mb-2">Yeni Müşteri</h3>
          <p className="text-3xl font-bold text-blue-600">{segments.new || 0}</p>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4 text-slate-900">Harcamaya Göre En İyi Müşteriler</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className={headerCellClass}>Müşteri</th>
                  <th className={headerCellClass}>
                    Firma
                  </th>
                  <th className={headerCellClass}>Toplam Harcama</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {segments.topCustomers?.map((customer, index) => (
                  <tr key={index} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                      {customer.name}
                    </td>
                    <td className={bodyCellClass}>
                      {customer.company || 'Yok'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                      {formatCurrency(customer.total_spent)}
                    </td>
                  </tr>
                )) || <tr><td colSpan="3" className="py-4 text-center text-slate-500">Müşteri bulunamadı</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const CustomerNotes = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotes();
  }, []);

  const fetchNotes = async () => {
    try {
      const response = await marketingAPI.getCustomerNotes();
      setNotes(response.data);
    } catch (error) {
      toast.error('Müşteri notları alınamadı');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Müşteri Notları</h1>
          <p className="mt-1 text-sm text-slate-500">İletişim geçmişi ve hatırlatma tarihlerini tablo üzerinde izleyin.</p>
        </div>
        <button className="flex items-center rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Not Ekle
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className={headerCellClass}>Müşteri</th>
                <th className={headerCellClass}>
                  Not
                </th>
                <th className={headerCellClass}>Hatırlatma Tarihi</th>
                <th className={headerCellClass}>Oluşturulma</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {notes.map((note) => (
                <tr key={note.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                    {note.customer_name}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {note.note}
                  </td>
                  <td className={bodyCellClass}>
                    {note.reminder_date ? new Date(note.reminder_date).toLocaleDateString('tr-TR') : 'Yok'}
                  </td>
                  <td className={bodyCellClass}>
                    {new Date(note.created_at).toLocaleDateString('tr-TR')}
                  </td>
                </tr>
              )) || <tr><td colSpan="4" className="py-4 text-center text-slate-500">Not bulunamadı</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const Reminders = () => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReminders();
  }, []);

  const fetchReminders = async () => {
    try {
      const response = await marketingAPI.getReminders();
      setReminders(response.data);
    } catch (error) {
      toast.error('Hatırlatmalar alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const sortedReminders = useMemo(
    () => [...reminders].sort((a, b) => new Date(a.reminder_date) - new Date(b.reminder_date)),
    [reminders]
  );

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Yaklaşan Hatırlatmalar</h1>
        <p className="mt-1 text-sm text-slate-500">Yaklaşan geri dönüşleri kart düzeninde hızlıca takip edin.</p>
      </div>
      
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
        {sortedReminders.map((reminder) => (
          <div key={reminder.id} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <Bell className="w-5 h-5 text-orange-500 mr-2" />
                <h3 className="font-semibold text-slate-900">{reminder.customer_name}</h3>
              </div>
              {reminder.customer_company && (
                <span className="text-sm text-slate-500">{reminder.customer_company}</span>
              )}
            </div>
            
            <p className="mb-4 text-sm text-slate-600">{reminder.note}</p>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Hatırlatma:</span>
              <span className="text-sm font-semibold text-orange-600">
                {new Date(reminder.reminder_date).toLocaleDateString('tr-TR')}
              </span>
            </div>
          </div>
        )) || <p className="text-slate-500">Yaklaşan hatırlatma yok</p>}
      </div>
    </div>
  );
};

export default MarketingLayout;
